import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import PhoneInTalkRoundedIcon from "@mui/icons-material/PhoneInTalkRounded";
import StoreRoundedIcon from "@mui/icons-material/StoreRounded";
import ScienceRoundedIcon from "@mui/icons-material/ScienceRounded";
import SmartphoneRoundedIcon from "@mui/icons-material/SmartphoneRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";

const differentiators = [
  { icon: InsightsRoundedIcon, title: "Daily Vital Tracking", description: "Monitor your health metrics daily with smart tracking and AI-powered insights." },
  { icon: AssignmentRoundedIcon, title: "Personalized Care Plans", description: "Custom health programs designed by experts based on your unique health profile." },
  { icon: CreditCardRoundedIcon, title: "Smart Membership Plans", description: "Flexible, affordable plans that grow with your family without insurance hassles." },
  { icon: PhoneInTalkRoundedIcon, title: "24/7 Doctor Availability", description: "Connect with your personal doctor anytime, anywhere via call, chat, or video." },
  { icon: StoreRoundedIcon, title: "Connected Pharmacies", description: "Get medications delivered to your doorstep from partner pharmacies." },
  { icon: ScienceRoundedIcon, title: "Discounted Labs", description: "Access premium lab tests at up to 60% discounted rates with home collection." },
  { icon: SmartphoneRoundedIcon, title: "Wellness App", description: "Manage your entire health journey from one intuitive mobile application." },
  { icon: PlaceRoundedIcon, title: "Doctor Anywhere", description: "Whether at home or traveling, your healthcare follows you everywhere." }
];

export default function WhatMakesDifferent() {
  return (
    <Box component="section" className="section-padding" sx={{ bgcolor: "background.default", position: "relative" }}>
      <Box className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={{ textAlign: "center", maxWidth: 768, margin: "0 auto 64px" }}
        >
          <Box
            component="span"
            sx={{
              display: "inline-block",
              px: 2,
              py: 0.75,
              borderRadius: 999,
              bgcolor: "#e4f3ee",
              color: "#1f7f72",
              fontSize: 14,
              fontWeight: 600,
              mb: 2
            }}
          >
            Our Advantage
          </Box>

          <Typography
            component="h2"
            sx={{
              fontSize: { xs: "1.875rem", md: "2.25rem", lg: "3rem" },
              fontWeight: 800,
              mb: 3,
              lineHeight: 1.15
            }}
          >
            What Makes Rite Wellness
            <Box component="span" className="gradient-text" sx={{ display: "block" }}>
              Different
            </Box>
          </Typography>

          <Typography sx={{ fontSize: "1.125rem", color: "text.secondary" }}>
            We've reimagined healthcare to be accessible, personal, and technology-driven while
            maintaining the warmth of traditional care.
          </Typography>
        </motion.div>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr 1fr" }, gap: 3, mb: 6 }}>
          {differentiators.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
              >
                <Box className="premium-card" sx={{ p: 3, height: "100%", textAlign: "center" }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "16px",
                      bgcolor: "rgba(39,165,153,0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 2,
                      transition: "background-color 300ms ease"
                    }}
                    className="diff-icon"
                  >
                    <Icon sx={{ fontSize: 28, color: "primary.main" }} className="diff-icon-svg" />
                  </Box>
                  <Typography sx={{ fontWeight: 700, mb: 1 }}>{item.title}</Typography>
                  <Typography sx={{ fontSize: 14, color: "text.secondary" }}>{item.description}</Typography>
                </Box>
              </motion.div>
            );
          })}
        </Box>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={{ textAlign: "center" }}
        >
          <Button
            component={Link}
            to="/signup"
            size="large"
            variant="contained"
            sx={{
              bgcolor: "primary.main",
              color: "#fff",
              boxShadow: "var(--shadow-glow)",
              "&:hover": { bgcolor: "rgba(39,165,153,0.9)" }
            }}
            endIcon={<ArrowForwardRoundedIcon sx={{ transition: "transform 200ms" }} />}
          >
            Start Your Wellness Journey
          </Button>
        </motion.div>
      </Box>
    </Box>
  );
}
